import { FC, ReactElement, ReactNode, useContext, useEffect, useId, useMemo, useState } from "react";
import BsModal, { ModalProps as BsModalProps } from "react-bootstrap/Modal";
import { useForm, FieldValues, UseFormProps, FormProvider, SubmitHandler } from "react-hook-form";
import { UUID } from "crypto";
import classNames from "classnames";

import { ModalToastHeader } from "../../toasts/modal-toast-header";
import { ModalDispatchContext, ModalStackStateContext } from "./modal-provider";
import { BasicFormProps, FormWithContext } from "../../forms/form-context-wrapper/form-context-wrapper";

/** Number of stack levels that have z-index rules in custom-shared.css. */
export const MAX_MODAL_STACK_DEPTH = 8;

/**
 * Classes that put a dialog and its backdrop on the same level of the stack.
 *
 * A modal and its backdrop are siblings in <body>, so both need a z-index and they have to agree
 * on the order. That order comes from how many modals are open rather than from where the elements
 * sit in <body>: the rules this replaces used :nth-of-type(), which counts every <div> in <body>,
 * so a WalkMe element, a browser extension or a leftover backdrop could push a backdrop above the
 * dialog it belonged to and lock the user out behind the grey overlay.
 *
 * Exported for the modals that cannot use BaseModal because they need their own markup inside the
 * header, body or footer. They still have to take part in stacking.
 */
export const modalStackClasses = (depth: number, superZ = false): string =>
  classNames(`modal-stack-${Math.min(Math.max(depth, 1), MAX_MODAL_STACK_DEPTH)}`, superZ && "modal-stack-super");

interface BaseModalProps extends BsModalProps {
  hideCloseButton?: boolean;
  title?: string;
  body?: ReactNode;
  footer?: ReactNode;
  superZ?: boolean;
  /** 1-based position in the stack of open modals. Used when stackId is not supplied. */
  depth?: number;
  /** Lets BaseModal read its own level so the owner of the dropdown does not subscribe to the stack. */
  stackId?: string;
}

export const BaseModal: FC<BaseModalProps> = ({
  hideCloseButton = false,
  superZ = false,
  depth: depthProp,
  stackId,
  title,
  body,
  footer,
  className,
  backdropClassName,
  ...bsModalProps
}) => {
  const id = useId();
  const modalId = `modal-${id}`;
  const labelId = `modal-label-${id}`;
  const openModals = useContext(ModalStackStateContext);
  const depth = stackId ? Math.max(openModals.indexOf(stackId) + 1, 1) : Math.max(depthProp ?? 1, 1);

  const stackClasses = modalStackClasses(depth, superZ);

  return (
    <BsModal
      scrollable={true}
      centered={true}
      backdrop="static"
      {...bsModalProps}
      id={modalId}
      aria-labelledby={labelId}
      className={classNames(stackClasses, className)}
      backdropClassName={classNames(stackClasses, backdropClassName)}
    >
      <BsModal.Header closeButton={!hideCloseButton}>
        <BsModal.Title as="h5">{title}</BsModal.Title>
      </BsModal.Header>
      <BsModal.Body>{body}</BsModal.Body>
      {!!footer && <BsModal.Footer>{footer}</BsModal.Footer>}
    </BsModal>
  );
};

interface UseModalProps {
  disableToast?: boolean;
  superZ?: boolean;
  onBeforeClose?: () => void;
}

// Identifies a modal within the open-modal stack. A counter rather than useId because useId
// would renumber the generated ids of every other component in the tree, and rather than the
// uuid below because that is mocked to a constant in tests.
let modalStackSequence = 0;

/**
 * Registers this modal on the app-wide stack while `isOpen`. Does not read the stack list, so
 * a parent that also owns a dropdown will not re-render when the stack changes.
 */
export const useModalStackRegistration = (isOpen: boolean): string => {
  const [stackId] = useState(() => `modal-${++modalStackSequence}`);
  const { setModalStacked } = useContext(ModalDispatchContext);

  useEffect(() => {
    setModalStacked(stackId, isOpen);
  }, [isOpen, stackId, setModalStacked]);

  // Leaving the stack on unmount is what lets the provider notice a modal that went away while
  // it was still open, and clean up the backdrop it left behind.
  useEffect(
    () => () => {
      setModalStacked(stackId, false);
    },
    [stackId, setModalStacked]
  );

  return stackId;
};

/**
 * Joins the app-wide modal stack while `isOpen`, and returns this modal's 1-based level in it.
 *
 * For owners that also render a dropdown, prefer useModal (which keeps the stack subscription
 * inside BaseModal) so the dropdown is not re-rendered mid-close.
 */
export const useModalStackDepth = (isOpen: boolean): number => {
  const stackId = useModalStackRegistration(isOpen);
  const openModals = useContext(ModalStackStateContext);
  return Math.max(openModals.indexOf(stackId) + 1, 1);
};

export const useModal = (useModalProps?: UseModalProps) => {
  // TODO: some time far in the future we may need to worry about lazy loading the modal
  const [showModal, setShowModal] = useState(false);
  const uuid = useMemo(() => crypto.randomUUID(), []);
  const { recordModalOpen } = useContext(ModalDispatchContext);

  useEffect(() => {
    if (!useModalProps?.disableToast) {
      recordModalOpen(uuid as UUID, showModal);
    }
  }, [showModal]);

  // Registers without reading the stack list. Depth is resolved inside BaseModal so this hook's
  // caller (for example StatusDropdown) does not re-render when another modal joins the stack.
  const stackId = useModalStackRegistration(showModal);

  const hide = () => {
    setShowModal(false);
  };
  const show = () => {
    setShowModal(true);
  };

  const handleHide = async (onHide?: () => void) => {
    if (useModalProps?.onBeforeClose) {
      useModalProps.onBeforeClose();
      return;
    }
    onHide?.();
    hide();
  };

  const renderModal: FC<BaseModalProps> = ({ body, show, onHide, ...props }) => (
    <BaseModal
      show={show === undefined ? showModal : show}
      onHide={() => handleHide(onHide)}
      body={
        <>
          {!useModalProps?.disableToast && <ModalToastHeader />}
          {body}
        </>
      }
      centered
      {...props}
      stackId={stackId}
    />
  );

  return {
    show,
    hide,
    Modal: renderModal,
    renderModal
  };
};

interface UseFormModalOptions<T extends FieldValues> {
  name: string;
  useFormProps: UseFormProps<T>;
  onSubmit: SubmitHandler<T>;
  onBeforeClose?: () => void;
}

export const useFormModal = <T extends FieldValues>({
  name,
  useFormProps,
  onSubmit,
  onBeforeClose
}: UseFormModalOptions<T>) => {
  const id = useId();
  const formId = `${name}-${id}`;
  const methods = useForm<T>(useFormProps);

  const { show, hide, renderModal } = useModal({ onBeforeClose });

  const handleHide = (onHide?: () => void) => {
    onHide?.();
    methods.reset();
    hide();
  };

  const renderFormModal: FC<BaseModalProps> = ({ body, onHide, ...props }) => {
    return (
      <FormProvider {...methods}>
        {renderModal({
          ...props,
          body: (
            <FormWithContext formId={formId} onSubmit={onSubmit as SubmitHandler<FieldValues>}>
              {body as ReactElement<BasicFormProps<FieldValues>>}
            </FormWithContext>
          ),
          onHide: () => handleHide(onHide)
        })}
      </FormProvider>
    );
  };

  return {
    show,
    hide: () => handleHide(),
    formId,
    renderModal: renderFormModal,
    methods
  };
};
